import React from 'react';

import { useTheme } from '/imports/ui/foundation/Theme';
import type { Align, Justify, Size, StyledSystemProp } from './styled-system';
import { alignItems, justifyContent, sizeToNumber, useStyledSystem } from './styled-system';
import type { PolymorphicComponentPropWithRef } from './type-utils';

export type StackProps<C extends React.ElementType> = PolymorphicComponentPropWithRef<
  C,
  StyledSystemProp & {
    justify?: Justify;
    align?: Align;
    gap?: Size | number | boolean;
    wrap?: boolean;
    marginTrim?: 'block';
  }
>;

export type StackComponent = <C extends React.ElementType = 'div'>(
  props: StackProps<C>,
) => React.ReactNode;

const Stack = <C extends React.ElementType = 'div'>({
  align,
  as,
  children,
  gap,
  justify,
  style,
  wrap,
  ...props
}: StackProps<C>) => {
  const { tokens } = useTheme();
  const Component = as ?? 'div';
  const elementStyles: React.CSSProperties = {
    justifyContent: justifyContent(justify),
    alignItems: alignItems(align),
    gap: sizeToNumber(tokens, 'size')(gap),
    flexWrap: wrap ? 'wrap' : 'nowrap',
    ...style,
  };
  return <Component {...useStyledSystem({ ...props, style: elementStyles })}>{children}</Component>;
};

export const HStack: StackComponent = <C extends React.ElementType = 'div'>(
  props: StackProps<C>,
) => <Stack {...props} display={props.display ?? 'flexRow'} />;

export const VStack: StackComponent = <C extends React.ElementType = 'div'>(
  props: StackProps<C>,
) => <Stack {...props} display={props.display ?? 'flexColumn'} />;
